import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import NavigationBar from '../../components/ui/NavigationBar';
import { Button } from '../../components/ui/Button';
import { RankChangeIndicator, RankBadgeWithChange } from '../../components/RankChangeIndicator';
import {
  getLeaderboard,
  getUserLeaderboardStats,
  getLeaderboardComparison,
  getUserRankHistory,
  getUserAchievements,
  getActiveSeason,
  LEADERBOARD_TIERS,
  LEADERBOARD_METRICS
} from '../../services/leaderboardService';

const LeaderboardPage = () => {
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTier, setActiveTier] = useState(LEADERBOARD_TIERS.GLOBAL);
  const [activeMetric, setActiveMetric] = useState(LEADERBOARD_METRICS.TOTAL_XP);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [rankHistory, setRankHistory] = useState([]);
  const [activeSeason, setActiveSeason] = useState(null);

  useEffect(() => {
    if (user) {
      loadLeaderboardData();
    }
  }, [user, activeTier, activeMetric]);

  const loadLeaderboardData = async () => {
    setLoading(true);
    try {
      // Load season
      const season = await getActiveSeason();
      setActiveSeason(season);

      // Load leaderboard
      const tierOptions = {};
      if (activeTier === LEADERBOARD_TIERS.REGIONAL) {
        tierOptions.region = profile?.region || 'North America';
      } else if (activeTier === LEADERBOARD_TIERS.TEAM) {
        tierOptions.teamId = profile?.primary_team_id;
      }

      const leaderboard = await getLeaderboard(activeTier, activeMetric, tierOptions);
      setLeaderboardData(leaderboard);

      // Load user stats
      const stats = await getUserLeaderboardStats(user.id);
      setUserStats(stats);

      // Load comparison
      const comp = await getLeaderboardComparison(user.id, activeMetric);
      setComparison(comp);

      // Load achievements
      const userAchievements = await getUserAchievements(user.id);
      setAchievements(userAchievements);

      // Load rank history
      const history = await getUserRankHistory(user.id, 30);
      setRankHistory(history);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case LEADERBOARD_TIERS.GLOBAL:
        return '🌍 Global';
      case LEADERBOARD_TIERS.REGIONAL:
        return '📍 Regional';
      case LEADERBOARD_TIERS.TEAM:
        return '👥 Team';
      default:
        return tier;
    }
  };

  const getMetricLabel = (metric) => {
    switch (metric) {
      case LEADERBOARD_METRICS.TOTAL_XP:
        return '⚡ Total XP';
      case LEADERBOARD_METRICS.OBJECTION_MASTERY:
        return '🎯 Objections Mastered';
      case LEADERBOARD_METRICS.CONSISTENCY_STREAK:
        return '🔥 Longest Streak';
      case LEADERBOARD_METRICS.PERFECT_SCORES:
        return '💯 Perfect Scores';
      case LEADERBOARD_METRICS.IMPROVEMENT_RATE:
        return '📈 Improvement Rate';
      default:
        return metric;
    }
  };

  const getMetricValue = (entry, metric) => {
    switch (metric) {
      case LEADERBOARD_METRICS.TOTAL_XP:
        return `${entry.total_xp?.toLocaleString() || 0} XP`;
      case LEADERBOARD_METRICS.OBJECTION_MASTERY:
        return entry.objections_mastered || 0;
      case LEADERBOARD_METRICS.CONSISTENCY_STREAK:
        return `${entry.longest_streak || 0} days`;
      case LEADERBOARD_METRICS.PERFECT_SCORES:
        return entry.perfect_scores || 0;
      case LEADERBOARD_METRICS.IMPROVEMENT_RATE:
        return `${entry.improvement_rate || 0}%`;
      default:
        return '-';
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getRankChange = (userId) => {
    if (rankHistory.length < 2) return null;
    const latest = rankHistory[rankHistory.length - 1];
    const previous = rankHistory[rankHistory.length - 2];
    
    if (activeTier === LEADERBOARD_TIERS.GLOBAL && latest.global_rank && previous.global_rank) {
      return {
        current: latest.global_rank,
        previous: previous.global_rank
      };
    } else if (activeTier === LEADERBOARD_TIERS.REGIONAL && latest.regional_rank && previous.regional_rank) {
      return {
        current: latest.regional_rank,
        previous: previous.regional_rank
      };
    } else if (activeTier === LEADERBOARD_TIERS.TEAM && latest.team_rank && previous.team_rank) {
      return {
        current: latest.team_rank,
        previous: previous.team_rank
      };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <NavigationBar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading leaderboard...</p>
          </div>
        </div></div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <NavigationBar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            🏆 Leaderboards
          </h1>
          {activeSeason && (
            <div className="flex items-center gap-3">
              <p className="text-gray-600">
                Current Season: <span className="font-semibold text-gray-900">{activeSeason.season_name}</span>
              </p>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {activeSeason.trophy_name} {activeSeason.badge_icon}
              </span>
            </div>
          )}
        </div>

        {/* User Stats Card */}
        {userStats && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border-2 border-blue-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Your Rankings</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Global Rank */}
              <div className="text-center p-4 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg">
                <div className="text-3xl mb-2">🌍</div>
                <div className="text-sm text-gray-600 mb-1">Global Rank</div>
                <div className="flex flex-col items-center gap-1">
                  <div className="text-3xl font-bold text-gray-900">
                    {userStats.global_rank ? getRankBadge(userStats.global_rank) : '-'}
                  </div>
                  {getRankChange(user?.id) && (
                    <RankChangeIndicator 
                      currentRank={getRankChange(user?.id).current}
                      previousRank={getRankChange(user?.id).previous}
                      size="small"
                    />
                  )}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {userStats.total_xp?.toLocaleString() || 0} XP
                </div>
              </div>

              {/* Regional Rank */}
              <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
                <div className="text-3xl mb-2">📍</div>
                <div className="text-sm text-gray-600 mb-1">Regional Rank</div>
                <div className="text-3xl font-bold text-gray-900">
                  {userStats.regional_rank ? getRankBadge(userStats.regional_rank) : '-'}
                </div>
                <div className="text-sm text-gray-500 mt-1">{userStats.region || 'No region'}</div>
              </div>

              {/* Team Rank */}
              <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg">
                <div className="text-3xl mb-2">👥</div>
                <div className="text-sm text-gray-600 mb-1">Team Rank</div>
                <div className="text-3xl font-bold text-gray-900">
                  {userStats.team_rank ? getRankBadge(userStats.team_rank) : '-'}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {userStats.team_id ? 'Your Team' : 'No team'}
                </div>
              </div>
            </div>

            {/* Achievements */}
            {achievements.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Achievements</h3>
                <div className="flex flex-wrap gap-2">
                  {achievements.slice(0, 5).map((achievement) => (
                    <span
                      key={achievement.id}
                      className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                      title={achievement.achievement_name}
                    >
                      {achievement.badge_icon} {achievement.achievement_name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tier Selector */}
        <div className="flex flex-wrap gap-3 mb-6">
          {Object.values(LEADERBOARD_TIERS).map((tier) => (
            <Button
              key={tier}
              variant={activeTier === tier ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTier(tier)}
            >
              {getTierLabel(tier)}
            </Button>
          ))}
        </div>

        {/* Metric Selector */}
        <div className="flex flex-wrap gap-3 mb-6">
          {Object.values(LEADERBOARD_METRICS).map((metric) => (
            <Button
              key={metric}
              variant={activeMetric === metric ? 'default' : 'outline'}
              size="xs"
              onClick={() => setActiveMetric(metric)}
            >
              {getMetricLabel(metric)}
            </Button>
          ))}
        </div>

        {/* Leaderboard Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {getMetricLabel(activeMetric)}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Streak
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total XP
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leaderboardData.map((entry) => {
                  const isCurrentUser = entry.user_id === user?.id;
                  return (
                    <tr
                      key={entry.id}
                      className={isCurrentUser ? 'bg-blue-50 border-l-4 border-blue-500' : ''}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-2xl font-bold">
                          {getRankBadge(entry.display_rank)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center text-white font-bold">
                            {entry.user?.full_name?.charAt(0) || '?'}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {entry.user?.full_name || 'Anonymous'}
                              {isCurrentUser && (
                                <span className="ml-2 text-blue-600 font-semibold">(You)</span>
                              )}
                            </div>
                            {entry.user?.company_name && (
                              <div className="text-sm text-gray-500">{entry.user.company_name}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {getMetricValue(entry, activeMetric)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          🔥 {entry.longest_streak || 0} days
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {entry.total_xp?.toLocaleString() || 0} XP
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {leaderboardData.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏆</div>
              <p className="text-gray-600 text-lg">No data yet for this leaderboard.</p>
              <p className="text-gray-500 text-sm mt-2">
                Complete drills and objections to see your ranking!
              </p>
            </div>
          )}
        </div>
      </div></div>
  );
};

export default LeaderboardPage;
